import EmptyState from '@pinecast/common/EmptyState';
import ErrorState from '@pinecast/common/ErrorState';
import Group from '@pinecast/common/Group';
import {RightArrowSmall} from '@pinecast/common/icons';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {usePodcastLang} from '@pinecast/common/PodcastString';
import Section from '@pinecast/common/Section';
import {useConfirmBox} from '@pinecast/common/Stage';
import {SectionSubheaderH2} from '@pinecast/common/Text';
import {usePageTitleEffect} from '@pinecast/db-spa/App/usePageTitle';
import {useQuery} from '@pinecast/gql';
import type {
  DonationPage as DonationPageQuery,
  DonationPage_podcast,
  DonationPage_tipJarSubscription,
  DonationPageVariables,
} from '@pinecast/gql/__generated_tj__/DonationPage';
import {NextRecurringTipStateEnum} from '@pinecast/gql/__generated_tj__/globalTypes';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {useCSS, useSetBackground} from '@pinecast/styles';
import gql from 'graphql-tag';
import * as React from 'react';
import {useParams} from 'react-router';

import DonationPageHeader from './DonationPageHeader';
import TipAmountSelection from './TipAmountSelection';
import type {Frequency} from './TipFrequency';
import TipFrequency from './TipFrequency';
import TipJarDone from './TipJarDone';
import TipJarFooter from './TipJarFooter';
import TipJarIncludes from './TipJarIncludes';
import TipJarPendingVerification from './TipJarPendingVerification';
import TipJarRequestLogin from './TipJarRequestLogin';
import TipJarSummary from './TipJarSummary';
import {useGetSubscriptionCheckoutSession} from './useGetSubscriptionCheckoutSession';
import {stripeKey, useStripe} from './useStripe';

// Section sets paddingTop/Right/Bottom/Left individually. Overriding with the
// `padding` shorthand mixes shorthand and longhand in one atomic style object,
// which Styletron cannot resolve and warns about, so these are longhand too.
const sectionPadding = {
  paddingTop: '20px',
  paddingRight: '20px',
  paddingBottom: '20px',
  paddingLeft: '20px',
};
const messagePadding = {
  paddingTop: '16px',
  paddingRight: '20px',
  paddingBottom: '16px',
  paddingLeft: '20px',
};

const messages = {
  title: defineMessage({
    description:
      'Title for podcast donation page. {podcast} is the name of the podcast',
    defaultMessage: 'Subscribe to {podcast}',
  }),
};

export const DONATION_PAGE = gql`
  query DonationPage($slug: ID!) {
    podcast(slug: $slug) {
      slug
      name
      language
      canHaveRecurringTips
      coverArtThumbnail(size: 288)
      feedbackActive
      hasPrivateEpisodes
      privateAccessMinSubscription
      tipLimit
      tipJarActive
      tipJarAccountId
      tipJarCurrency
      tipJarMessage
      tipJarSuccessMessage
    }
    tipJarSubscription(podcastSlug: $slug) {
      id
      amount
      currency
      willCancel
    }
  }
`;

export type Step =
  | 'initial'
  | 'require_login'
  | 'done'
  | 'pending_verification';

const DonationPage = () => {
  const params = useParams<{slug: string}>();

  // React Router v6 useParams can return undefined values
  if (!params.slug) {
    throw new Error('Missing required route parameter: slug');
  }
  const css = useCSS();
  useSetBackground();
  const query = useQuery<DonationPageQuery, DonationPageVariables>(
    DONATION_PAGE,
    {variables: {slug: params.slug}},
  );

  usePageTitleEffect(() => {
    if (!query.data?.podcast) {
      return;
    }
    return [messages.title, {podcast: query.data.podcast.name}];
  }, [query]);
  const podcastLang = usePodcastLang(query.data?.podcast);

  // The page's one main landmark. The tip jar's Django shell has none, and
  // these pages do not render `Chrome`, which has the main of /subscriptions.
  return (
    <main
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        margin: '24px auto 40px',
        maxWidth: '720px',
        padding: '0 16px',
      })}
    >
      <DonationPageHeader podcast={query.data?.podcast ?? null} />
      {query.loading ? (
        <Section>
          <LoadingState
            title={
              <FormattedMessage
                description="Message for when the donation page content is loading"
                defaultMessage="Loading podcast information…"
              />
            }
          />
        </Section>
      ) : query.error ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Message for when the donation page content has errored"
                defaultMessage="Unable to load podcast"
              />
            }
          />
        </Section>
      ) : !query.data.podcast || !query.data.podcast.tipJarActive ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Message for when a tip jar is inactive"
                defaultMessage="This podcast's tip jar is not available."
              />
            }
            copy={
              <FormattedMessage
                description="Copy for when a tip jar is inactive"
                defaultMessage="Contact the show's owner for support."
              />
            }
          />
        </Section>
      ) : (
        <>
          {query.data.tipJarSubscription && (
            <Link
              href={`/subscriptions/recurring_tips/${query.data.tipJarSubscription.id}`}
              style={{display: 'block', textAlign: 'center'}}
            >
              <Group spacing={4} centerChildren>
                <FormattedMessage
                  description="Link to manage existing tip jar subscription"
                  defaultMessage="Manage your existing subscription"
                />
                <RightArrowSmall />
              </Group>
            </Link>
          )}
          {query.data.podcast.tipJarMessage && (
            <Section style={messagePadding}>
              <SectionSubheaderH2>
                <FormattedMessage
                  description="Heading above the message a podcaster has written for their tip jar page"
                  defaultMessage="From the show"
                />
              </SectionSubheaderH2>
              {/* The podcaster's words, in the podcast's language. */}
              <div
                className={css({fontSize: '15px', lineHeight: '22px'})}
                dir="auto"
                lang={podcastLang}
              >
                {query.data.podcast.tipJarMessage}
              </div>
            </Section>
          )}
          {query.data.tipJarSubscription?.willCancel ? (
            <Section>
              <EmptyState
                title={
                  <FormattedMessage
                    description="Title when there is an existing subscription that will cancel"
                    defaultMessage="You have an existing subscription set to cancel"
                  />
                }
                copy={
                  <FormattedMessage
                    description="Copy when there is an existing subscription that will cancel"
                    defaultMessage="You've previously set up a subscription to this podcast that's set to cancel automatically at the end of the current billing period. If you'd like to change this subscription, make it recurring first."
                  />
                }
                actionLabel={
                  <FormattedMessage
                    description="Button when there is an existing subscription that will cancel"
                    defaultMessage="Update subscription"
                  />
                }
                onAction={`/subscriptions/recurring_tips/${query.data.tipJarSubscription.id}`}
              />
            </Section>
          ) : (
            <DonationPageInner
              podcast={query.data.podcast}
              subscription={query.data.tipJarSubscription}
            />
          )}
        </>
      )}
      {/* A subscription comes back only for a tipper who is signed in. */}
      <TipJarFooter signedIn={!!query.data?.tipJarSubscription} />
    </main>
  );
};

const DonationPageInner = ({
  podcast,
  subscription,
}: {
  podcast: DonationPage_podcast;
  subscription: DonationPage_tipJarSubscription | null;
}) => {
  const css = useCSS();
  const stripe = useStripe(stripeKey, podcast.tipJarAccountId!);
  const confirm = useConfirmBox();

  const sameCurrency = subscription?.currency === podcast.tipJarCurrency;
  const [step, setStep] = React.useState<Step>('initial');
  const [amount, setAmount] = React.useState<number | null>(
    sameCurrency ? subscription!.amount : null,
  );
  // An existing subscription is already recurring; there is nothing to ask.
  const [frequency, setFrequency] = React.useState<Frequency>('recurring');
  // Set from the request until the browser has left for Stripe, or failed to.
  const [pending, setPending] = React.useState(false);
  // The session was made, but the browser could not get to Stripe's page.
  const [stripeFailed, setStripeFailed] = React.useState(false);

  const getSubscriptionCheckoutSession = useGetSubscriptionCheckoutSession();

  const effectiveFrequency: Frequency = subscription ? 'recurring' : frequency;
  // A subscriber starts on the amount they give now, and changing it to itself
  // would change nothing.
  const unchanged = sameCurrency && amount === subscription!.amount;

  // A subscriber changes what they are charged, so they say so first (WCAG
  // 3.3.4). The confirm box never settles when they cancel.
  async function confirmChange(newAmount: number) {
    const current = formatCurrency(
      subscription!.amount,
      subscription!.currency,
    );
    const next = formatCurrency(newAmount, podcast.tipJarCurrency);
    if (sameCurrency) {
      await confirm(
        <FormattedMessage
          description="Title of the confirmation box on the tip jar page when a subscriber changes how much they give each month"
          defaultMessage="Change your monthly contribution?"
        />,
        <FormattedMessage
          description="Copy of the confirmation box on the tip jar page when a subscriber changes how much they give each month. {current} and {next} are formatted currency amounts."
          defaultMessage="Your contribution will change from {current} to {next} a month, starting with your next charge."
          values={{current, next}}
        />,
        <FormattedMessage
          description="Button in the confirmation box on the tip jar page that changes how much a subscriber gives each month"
          defaultMessage="Change amount"
        />,
      );
      return;
    }
    // The show takes another currency now. A card cannot move to it, so the
    // subscriber pays again on Stripe's page, and the server sets the old
    // subscription to end before that page opens.
    await confirm(
      <FormattedMessage
        description="Title of the confirmation box on the tip jar page when a subscriber has to replace their subscription, because the show now takes another currency"
        defaultMessage="Replace your subscription?"
      />,
      <FormattedMessage
        description="Copy of the confirmation box on the tip jar page when a subscriber has to replace their subscription, because the show now takes another currency. {current} and {next} are formatted currency amounts."
        defaultMessage="This show takes payments in another currency now. Your subscription of {current} a month will end when its billing period ends, even if you don't finish paying for the new one. Next, you'll go to Stripe to pay {next} a month."
        values={{current, next}}
      />,
      <FormattedMessage
        description="Button in the confirmation box on the tip jar page that ends a subscriber's subscription and opens the payment page for a new one"
        defaultMessage="Continue to payment"
      />,
    );
  }

  async function handleContinue() {
    if (amount === null || pending || unchanged) {
      return;
    }
    if (subscription) {
      await confirmChange(amount);
    }
    setPending(true);
    setStripeFailed(false);
    try {
      const result = await getSubscriptionCheckoutSession.action({
        variables: {
          podcastSlug: podcast.slug,
          amount,
          oneTime: effectiveFrequency === 'oneTime',
        },
      });
      if (!result) {
        // The mutation holds the error, which the summary shows.
        return;
      }
      const session = result.createSubscriptionCheckoutSession;
      if (session?.nextState !== NextRecurringTipStateEnum.READY) {
        if (session) {
          setStep(session.nextState.toLowerCase() as Step);
        } else {
          setStripeFailed(true);
        }
        return;
      }
      let failed = true;
      try {
        // Null when Stripe.js could not load, which a content blocker can
        // cause.
        const stripeJs = await stripe;
        if (session.sessionId && stripeJs) {
          // On success the browser leaves this page, so this only settles
          // with an error.
          failed = !!(
            await stripeJs.redirectToCheckout({sessionId: session.sessionId})
          ).error;
        }
      } catch {
        // `loadStripe` rejects when its script fails to load.
      }
      setStripeFailed(failed);
    } finally {
      setPending(false);
    }
  }

  if (step === 'require_login') {
    return (
      <Section style={sectionPadding}>
        <TipJarRequestLogin
          slug={podcast.slug}
          amount={amount!}
          oneTime={effectiveFrequency === 'oneTime'}
          onContinue={setStep}
        />
      </Section>
    );
  }
  if (step === 'pending_verification') {
    return (
      <Section style={sectionPadding}>
        <TipJarPendingVerification />
      </Section>
    );
  }
  if (step === 'done') {
    return (
      <Section style={sectionPadding}>
        <TipJarDone />
      </Section>
    );
  }

  return (
    <>
      <TipAmountSelection
        onChange={setAmount}
        podcast={podcast}
        subscription={subscription}
        value={amount}
      />
      <TipJarIncludes podcast={podcast} />
      <Section style={sectionPadding}>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          })}
        >
          {!subscription && (
            <TipFrequency onChange={setFrequency} value={frequency} />
          )}
          <TipJarSummary
            amount={amount}
            error={
              getSubscriptionCheckoutSession.error ||
              (stripeFailed ? (
                <FormattedMessage
                  description="Error shown on the tip jar page when the Stripe page to pay for a contribution could not be opened"
                  defaultMessage="We couldn't open the payment page. Try again later."
                />
              ) : null)
            }
            frequency={effectiveFrequency}
            onContinue={handleContinue}
            pending={pending}
            podcast={podcast}
            unchanged={unchanged}
          />
        </div>
      </Section>
    </>
  );
};

export default DonationPage;
